import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  TestCaseExtendedResponse,
  TestCaseResponse,
  TestCasesResponse,
  TestCaseType,
  TestHistory,
  TestHistoryResponse,
  TestRunResponse,
  TestRunsResponse,
  TestSetBatchCreateRequest,
  TestSetBatchCreateResponse,
  TestSetResponse,
  TestSetsResponse,
} from './types';

export const NUM_TEST_HISTORY_PER_PAGE = 30;
export const NUM_TEST_CASES_PER_PAGE = 50;

export const getTestCase = ({
  accountId,
  projectId,
  id,
}: {
  accountId: string;
  projectId: string;
  id: string;
}) => {
  return doGet<TestCaseResponse>(
    generateNextPath(ENDPOINTS.TEST_CASE, {
      accountId,
      projectId,
      id,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const getTestCaseExtended = ({
  accountId,
  projectId,
  id,
  query,
}: {
  accountId: string;
  projectId: string;
  id: string;
  query?: string;
}) => {
  const path = generateNextPath(ENDPOINTS.TEST_CASE_EXTENDED, {
    accountId,
    projectId,
    id,
  });
  return doGet<TestCaseExtendedResponse>(path, LOGGER_TYPE.JUPITER, {
    version: 'v2',
    query,
  });
};

export const getTestCases = ({
  accountId,
  projectId,
  limit = NUM_TEST_CASES_PER_PAGE,
  query,
}: {
  accountId: string;
  projectId: string;
  limit?: number;
  offset?: number;
  query: string;
}) => {
  return doGet<TestCasesResponse>(
    generateNextPath(ENDPOINTS.TEST_CASES, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      params: {
        limit,
      },
      query,
      version: 'v2',
    },
  );
};

export const createTestCase = ({
  accountId,
  projectId,
  name,
  config,
  type,
}: {
  accountId: string;
  projectId: string;
  name: string;
  config: TestCaseResponse['config'];
  type: TestCaseType;
}) => {
  return doPost<TestCaseResponse>(
    generateNextPath(ENDPOINTS.TEST_CASES, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      name,
      config,
      type,
    },
    { version: 'v2' },
  );
};

export const updateTestCase = ({
  accountId,
  projectId,
  id,
  name,
  config,
  type,
}: {
  accountId: string;
  projectId: string;
  id: string;
  name: string;
  config: TestCaseResponse['config'];
  type: TestCaseType;
}) => {
  return doPatch<TestCaseResponse>(
    generateNextPath(ENDPOINTS.TEST_CASE, {
      accountId,
      projectId,
      id,
    }),
    LOGGER_TYPE.JUPITER,
    {
      name,
      config,
      type,
    },
    { version: 'v2' },
  );
};

export const deleteTestCase = ({
  accountId,
  projectId,
  id,
}: {
  accountId: string;
  projectId: string;
  id: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.TEST_CASE, { accountId, projectId, id }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const deleteTestCases = ({
  accountId,
  projectId,
  ids,
}: {
  accountId: string;
  projectId: string;
  ids: string[];
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.TEST_CASES, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    { test_case_ids: ids },
    { version: 'v2' },
  );
};

export const getTestHistory = ({
  accountId,
  projectId,
  id,
  limit = NUM_TEST_HISTORY_PER_PAGE,
  offset,
}: {
  accountId: string;
  projectId: string;
  id: string;
  limit?: number;
  offset?: number;
}) => {
  return doGet<TestHistoryResponse>(
    generateNextPath(ENDPOINTS.TEST_HISTORY, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      params: {
        test_case_id: id,
        limit,
        offset,
      },
      version: 'v2',
    },
  );
};

export const triggerTest = ({
  accountId,
  projectId,
  id,
  branchId,
  deploymentId,
}: {
  accountId: string;
  projectId: string;
  id: string;
  branchId?: string;
  deploymentId?: string;
}) => {
  return doPost<TestHistory>(
    generateNextPath(ENDPOINTS.TRIGGER_TEST, { accountId, projectId, id }),
    LOGGER_TYPE.AGENT_STREAM,
    {
      deployment_id: deploymentId,
      branch_id: branchId,
    },
    { version: 'v1', prefix: 'sourcerer' },
  );
};

export const getTestRuns = ({
  accountId,
  projectId,
  testSetId,
}: {
  accountId: string;
  projectId: string;
  testSetId?: string;
}) => {
  return doGet<TestRunsResponse>(
    generateNextPath(ENDPOINTS.TEST_RUNS, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    { params: { test_set_id: testSetId }, version: 'v2' },
  );
};

export const createTestRun = ({
  accountId,
  projectId,
  ids,
  name,
  branchId,
  deploymentId,
  runAll,
}: {
  accountId: string;
  projectId: string;
  ids: string[];
  name: string;
  branchId?: string;
  deploymentId?: string;
  runAll?: boolean;
}) => {
  return doPost<TestRunResponse>(
    generateNextPath(ENDPOINTS.TEST_RUNS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.AGENT_STREAM,
    {
      run_all_test_cases: runAll,
      test_run_name: name,
      branch_id: branchId,
      deployment_id: deploymentId,
      test_case_ids: ids,
    },
    { version: 'v1', prefix: 'sourcerer' },
  );
};

export const getTestRun = ({
  accountId,
  projectId,
  id,
}: {
  accountId: string;
  projectId: string;
  id: string;
}) => {
  return doGet<TestRunResponse>(
    generateNextPath(ENDPOINTS.TEST_RUN, { accountId, projectId, id }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const rerunTestRun = ({
  accountId,
  projectId,
  testRunId,
  name,
  branchId,
  deploymentId,
  ignoreMissingTestCases,
}: {
  accountId: string;
  projectId: string;
  testRunId: string;
  name: string;
  branchId?: string;
  deploymentId?: string;
  ignoreMissingTestCases?: boolean;
}) => {
  return doPost<TestRunResponse>(
    generateNextPath(ENDPOINTS.RERUN_TEST_RUN, {
      accountId,
      projectId,
      id: testRunId,
    }),
    LOGGER_TYPE.AGENT_STREAM,
    {
      test_run_name: name,
      branch_id: branchId,
      deployment_id: deploymentId,
      ignore_missing_test_cases: ignoreMissingTestCases,
    },
    { version: 'v1', prefix: 'sourcerer' },
  );
};

// Test Sets
export const getTestSets = ({
  accountId,
  projectId,
  query,
}: {
  accountId: string;
  projectId: string;
  query: string;
}) => {
  return doGet<TestSetsResponse>(
    generateNextPath(ENDPOINTS.TEST_SETS, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    { query, version: 'v2' },
  );
};

export const getTestSet = ({
  accountId,
  projectId,
  id,
}: {
  accountId: string;
  projectId: string;
  id: string;
}) => {
  return doGet<TestSetResponse>(
    generateNextPath(ENDPOINTS.TEST_SET, {
      accountId,
      projectId,
      id,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const createTestSet = ({
  accountId,
  projectId,
  name,
}: {
  accountId: string;
  projectId: string;
  name: string;
}) => {
  return doPost<TestSetResponse>(
    generateNextPath(ENDPOINTS.TEST_SETS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      name,
    },
    { version: 'v2' },
  );
};

export const patchTestSet = ({
  accountId,
  projectId,
  id,
  name,
  deployment_config,
}: {
  accountId: string;
  projectId: string;
  id: string;
  name?: string;
  deployment_config?: {
    sandbox?: boolean;
    'pre-release'?: boolean;
    live?: boolean;
  };
}) => {
  return doPatch<TestSetResponse>(
    generateNextPath(ENDPOINTS.TEST_SET, {
      accountId,
      projectId,
      id,
    }),
    LOGGER_TYPE.JUPITER,
    {
      ...(name && { name }),
      ...(deployment_config && { deployment_config }),
    },
    { version: 'v2' },
  );
};

export const deleteTestSet = ({
  accountId,
  projectId,
  id,
}: {
  accountId: string;
  projectId: string;
  id: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.TEST_SET, { accountId, projectId, id }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const createTestSetBatch = ({
  accountId,
  projectId,
  payload,
}: {
  accountId: string;
  projectId: string;
  payload: TestSetBatchCreateRequest;
}) => {
  return doPost<TestSetBatchCreateResponse>(
    generateNextPath(ENDPOINTS.TEST_SETS_BATCH, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    payload,
    { version: 'v2' },
  );
};

export const createTestSetRun = ({
  accountId,
  projectId,
  id,
  name,
  branchId,
  deploymentId,
}: {
  accountId: string;
  projectId: string;
  id: string;
  name: string;
  branchId?: string;
  deploymentId?: string;
}) => {
  return doPost<TestRunResponse>(
    generateNextPath(ENDPOINTS.TRIGGER_TEST_SET, { accountId, projectId, id }),
    LOGGER_TYPE.AGENT_STREAM,
    { test_run_name: name, deployment_id: deploymentId, branch_id: branchId },
    { version: 'v1', prefix: 'sourcerer' },
  );
};

export const duplicateTestSet = ({
  accountId,
  projectId,
  id,
  name,
}: {
  accountId: string;
  projectId: string;
  id: string;
  name: string;
}) => {
  return doPost<TestSetResponse>(
    generateNextPath(ENDPOINTS.DUPLICATE_TEST_SET, {
      accountId,
      projectId,
      id,
    }),
    LOGGER_TYPE.JUPITER,
    {
      name,
    },
    { version: 'v2' },
  );
};

export const removeTestCases = ({
  accountId,
  projectId,
  testSetId,
  testCaseIds,
}: {
  accountId: string;
  projectId: string;
  testSetId: string;
  testCaseIds: string[];
}) => {
  return doPatch<TestSetResponse>(
    generateNextPath(ENDPOINTS.REMOVE_TEST_CASES, {
      accountId,
      projectId,
      id: testSetId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      test_case_ids: testCaseIds,
    },
    { version: 'v2' },
  );
};
